import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaInfoCircle, FaExternalLinkAlt } from "react-icons/fa";

import { OperatorMachinesHelpContent } from "./operatorMachinesHelpContent";
import { fetchChecklistReports } from "./fieldAppApi";
import { FieldAppChecklistReportModal } from "./FieldAppChecklistReports";
import {
  buildMachineOperationMap,
  extractAssumeTimestamp,
  extractHourMeter,
  extractMachineIdent,
  extractPanelHectares,
  groupAssumeEventsByMachine,
} from "./fieldAppMachineUtils";

import "./FieldAppCalibrationPanel.css";
import "./FieldAppMachinesPanel.css";
import "./FieldAppChecklistReports.css";

function sameMachineIdent(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function findChecklistReport(reports, machineIdent, assumedAt) {
  const candidates = (reports || []).filter((report) =>
    sameMachineIdent(report.machine, machineIdent)
  );
  if (!candidates.length) return null;
  const assumeTime = assumedAt ? new Date(assumedAt).getTime() : NaN;
  if (Number.isNaN(assumeTime)) return candidates[0];
  return candidates.reduce((best, report) => {
    const current = Math.abs(new Date(report.created_at).getTime() - assumeTime);
    const bestDiff = Math.abs(new Date(best.created_at).getTime() - assumeTime);
    return current < bestDiff ? report : best;
  }, candidates[0]);
}

function formatHourMeterValue(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

export default function FieldAppMachinesPanel({
  farmId,
  rows,
  machines,
  formatDateTime,
  onOpenDetail,
  t,
}) {
  const [reports, setReports] = useState([]);
  const [openReport, setOpenReport] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchChecklistReports(farmId);
        if (!cancelled) setReports(data);
      } catch (error) {
        if (!cancelled) setReports([]);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  const operationMap = useMemo(
    () => buildMachineOperationMap(machines),
    [machines]
  );
  const machineGroups = useMemo(() => groupAssumeEventsByMachine(rows), [rows]);

  return (
    <div className="field-app-machines">
      <div className="field-app-calibration-help" role="note">
        <FaInfoCircle className="field-app-calibration-help-icon" aria-hidden />
        <div className="field-app-calibration-help-body">
          <strong>{t("Para que serve esta ferramenta?")}</strong>
          <OperatorMachinesHelpContent />
        </div>
      </div>

      {machineGroups.length === 0 ? (
        <p className="field-app-machines-empty text-muted">
          {t(
            "Nenhuma máquina assumida no período. Amplie as datas ou confirme se o tablet sincronizou os eventos ASSUMIR_MAQUINA."
          )}
        </p>
      ) : (
        <>
          <div className="field-app-machines-summary">
            <h3>{t("Máquinas operadas no período")}</h3>
            <div className="field-app-machines-chip-grid">
              {machineGroups.map((group) => {
                const meta = operationMap[group.machineIdent] || {};
                const operationId = meta.monitoringOperationId;
                const lastReport = findChecklistReport(
                  reports,
                  group.machineIdent,
                  group.lastAssumedAt
                );
                return (
                  <div
                    key={group.machineIdent}
                    className="field-app-machines-chip"
                  >
                    <div className="field-app-machines-chip-main">
                      <strong>{group.machineIdent}</strong>
                      <span>
                        {group.count}{" "}
                        {group.count === 1
                          ? t("assunção")
                          : t("assunções")}
                      </span>
                      <span className="field-app-machines-chip-date">
                        {t("Última")}: {formatDateTime(group.lastAssumedAt)}
                      </span>
                      <span
                        className={
                          lastReport
                            ? "field-app-machines-checklist is-done"
                            : "field-app-machines-checklist is-missing"
                        }
                      >
                        {lastReport
                          ? t("Checklist concluído")
                          : t("Checklist não feito")}
                      </span>
                    </div>
                    <div className="field-app-machines-chip-actions">
                      {operationId ? (
                        <Link
                          className="btn btn-sm btn-default field-app-machines-op-link"
                          to={`/${farmId}/operation/${operationId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaExternalLinkAlt aria-hidden />
                          {t("Ver em Operações")}
                        </Link>
                      ) : null}
                      {lastReport ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => setOpenReport(lastReport)}
                        >
                          {t("Ver checklist")}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped jambo_table">
              <thead>
                <tr>
                  <th>{t("Data/hora")}</th>
                  <th>{t("Operador")}</th>
                  <th>{t("Máquina")}</th>
                  <th>{t("Horímetro")}</th>
                  <th>{t("Hectares painel")}</th>
                  <th>{t("Foto")}</th>
                  <th>{t("Checklist")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const payload = row.payloadData || {};
                  const machineIdent = extractMachineIdent(payload);
                  const operationId =
                    (operationMap[machineIdent] || {}).monitoringOperationId ||
                    null;
                  const report = findChecklistReport(
                    reports,
                    machineIdent,
                    extractAssumeTimestamp(row)
                  );

                  return (
                    <tr key={row.uuid}>
                      <td>{formatDateTime(extractAssumeTimestamp(row))}</td>
                      <td>{row.operatorName || "—"}</td>
                      <td>
                        <strong>{machineIdent || "—"}</strong>
                      </td>
                      <td>{formatHourMeterValue(extractHourMeter(payload))}</td>
                      <td>
                        {extractPanelHectares(payload) != null
                          ? Number(extractPanelHectares(payload)).toLocaleString(
                              "pt-BR",
                              { maximumFractionDigits: 1 }
                            )
                          : "—"}
                      </td>
                      <td>
                        {row.hasPanelPhoto || row.panelPhotoUrl ? (
                          <span
                            className="field-app-fuel-photo-badge"
                            title={t("Foto do painel disponível")}
                          >
                            📷 {t("Sim")}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {report ? (
                          <span className="field-app-machines-checklist is-done">
                            {t("Concluído")}
                          </span>
                        ) : (
                          <span className="field-app-machines-checklist is-missing">
                            {t("Não feito")}
                          </span>
                        )}
                      </td>
                      <td className="field-app-machines-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-default"
                          onClick={() => onOpenDetail(row)}
                        >
                          {t("Detalhe")}
                        </button>
                        {operationId ? (
                          <Link
                            className="btn btn-sm btn-primary"
                            to={`/${farmId}/operation/${operationId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("Operações")}
                          </Link>
                        ) : null}
                        {report ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-default"
                            onClick={() => setOpenReport(report)}
                          >
                            {t("Ver checklist")}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <FieldAppChecklistReportModal
        report={openReport}
        onClose={() => setOpenReport(null)}
        t={t}
      />
    </div>
  );
}
